import React, { useEffect, useState } from "react";
import { CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { listIssueLogs } from "../../../../../services/issueLogService";
import {
  SOURCE_TYPE,
  ISSUE_STATUS,
} from "../../ProjectLogIssues/constants";
import ProjectListIssues from "../../ProjectLogIssues/ProjectListIssues";
import ProjectCardIssues from "../../ProjectLogIssues/ProjectCardIssues";
import AddIssueLogModal from "./AddIssueLogModal";
import IssueDetailModal from "./IssueDetailModal";
import CAutocomplete from "../../../../../components/CAutocomplete";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: ISSUE_STATUS.OPEN, label: "Open" },
  { value: ISSUE_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: ISSUE_STATUS.DONE, label: "Done" },
  { value: ISSUE_STATUS.RESOLVED, label: "Resolved" },
];

const primaryBtn = {
  height: 40,
  padding: "0 18px",
  borderRadius: 10,
  border: "none",
  background: "#4D6BF6",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "Inter Tight",
};

export default function IssuesSection({
  projectId,
  updateId,
  activities = [],
  disabled = false,
  reviewMode = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);

  const loadList = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await listIssueLogs(projectId, {
        page,
        size: 10,
        sortBy: "date",
        sortDirection: "DESC",
        status: status || undefined,
        sourceType: SOURCE_TYPE.DAILY_UPDATE,
      });
      setPageData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, status, page]);

  const issues = pageData?.content ?? [];
  const totalPages = pageData?.totalPages ?? 0;

  const selectedStatus =
    STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 4px" : "0 4px",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: "#0d0d0e",
            fontFamily: "Inter Tight",
          }}
        >
          Issues
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!reviewMode && (
            <div style={{ minWidth: 200 }}>
              <CAutocomplete
                options={STATUS_OPTIONS}
                value={selectedStatus}
                onChange={(_e, v) => {
                  setStatus(v?.value ?? "");
                  setPage(0);
                }}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                disableClearable
                placeholder="All"
              />
            </div>
          )}

          {!disabled && updateId && (
            <button
              type="button"
              style={primaryBtn}
              onClick={() => setLogOpen(true)}
            >
              Log Issue
            </button>
          )}
        </div>
      </div>

      {loading && !pageData && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && issues.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "#949daa",
            fontSize: 14,
          }}
        >
          No issues logged.
        </div>
      )}

      {issues.length > 0 &&
        (isMobile ? (
          <ProjectCardIssues list={issues} onIssueClick={setSelectedIssueId} />
        ) : (
          <ProjectListIssues
            list={issues}
            onIssueClick={setSelectedIssueId}
            hideSourceColumn
          />
        ))}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "12px" : "16px 4px",
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 13, color: "#949daa" }}>
            Page {page + 1} of {totalPages}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={pageData?.first}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: pageData?.first ? "not-allowed" : "pointer",
                opacity: pageData?.first ? 0.6 : 1,
                fontSize: 13,
                color: "#0d0d0e",
              }}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pageData?.last}
              onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: pageData?.last ? "not-allowed" : "pointer",
                opacity: pageData?.last ? 0.6 : 1,
                fontSize: 13,
                color: "#0d0d0e",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {logOpen && (
        <AddIssueLogModal
          projectId={projectId}
          updateId={updateId}
          activities={activities}
          onClose={() => setLogOpen(false)}
          onSaved={() => {
            setLogOpen(false);
            loadList();
          }}
        />
      )}

      {selectedIssueId && (
        <IssueDetailModal
          issueId={selectedIssueId}
          onClose={() => setSelectedIssueId(null)}
          onChanged={() => {
            setSelectedIssueId(null);
            loadList();
          }}
        />
      )}
    </div>
  );
}
